import { DealConfidenceLevelAPI } from '@business/api/deal';
import { DealConfidenceLevelId, DealConfidenceLevelResponse } from '@business/entities/deal';
import { DealConfidenceLevelData } from '@business/api/deal/deal_confidence_level_api';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap } from 'rxjs/operators';

export class DealConfidenceLevelBloc extends BaseBloc {
  private readonly configRepo = new Repository<DealConfidenceLevelResponse>({
    getItemId: item => item.id,
  });

  readonly items$ = this.configRepo.items$.pipe(map(items => R.sortBy(items, 'menuOrder')));
  readonly selectItem = this.configRepo.selectItem;

  onReset() {
    this.configRepo.reset();
  }

  getColorByKey(key: string): string {
    const config = this.configRepo.items.find(c => c.key === key);
    return config?.color ?? '#74788d';
  }

  fetchDealConfidenceLevels = () => {
    return DealConfidenceLevelAPI.fetchDealConfidenceLevels().pipe(
      this.configRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_deal_confidence_levels_error') }),
    );
  };

  createDealConfidenceLevel = (data: DealConfidenceLevelData) => {
    return DealConfidenceLevelAPI.createDealConfidenceLevel(data).pipe(
      this.configRepo.ops.reset(),
      handleMessage({ type: requestMessage('create_deal_confidence_level') }),
    );
  };

  updateDealConfidenceLevel = (id: DealConfidenceLevelId, data: DealConfidenceLevelData) => {
    return DealConfidenceLevelAPI.updateDealConfidenceLevel(id, data).pipe(
      this.configRepo.ops.reset(),
      handleMessage({ type: requestMessage('update_deal_confidence_level') }),
    );
  };

  deleteDealConfidenceLevel = (id: DealConfidenceLevelId) => {
    return showDeleteConfirmation('Delete Confidence Level', 'Do you really want to remove this confidence level?').pipe(
      switchMap(() => DealConfidenceLevelAPI.deleteDealConfidenceLevel(id)),
      this.configRepo.ops.removeOne(() => id),
      handleMessage({ type: requestMessage('delete_deal_confidence_level') }),
    );
  };

  sortDealConfidenceLevels = (ids: DealConfidenceLevelId[]) => {
    return DealConfidenceLevelAPI.sortDealConfidenceLevels(ids).pipe(
      this.configRepo.ops.reset(),
      handleMessage({ type: requestMessage('update_deal_confidence_level') }),
    );
  };
}
